import React, { ChangeEvent, useCallback, useState } from 'react';
import { useLanguage } from '../../../../locale/useLanguage';
import { CancelFilledIcon, SearchIcon } from '../../../../components/Icons';

import './InsertAddress.css';
import { PredictionsEntity, ResultsEntity } from '../../../../models';
import { Button } from '../../../../components/Button/Button';
import {
  AddressParts,
  useGoogleApiDesktop,
} from '../../../../hooks/useDesktopGoogleApi';
import { isNil } from 'lodash';
import { useIsValidAddressMutation } from '../../../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../../../apis/utils';

enum AddressError {
  MissingInfo = 'missing-info',
  AddressNotSupported = 'address-not-supported',
}

type InsertAddressProps = {
  insertedAddress?: AddressParts;
  territoryId?: string;
  onContinue: (address: AddressParts) => void;
};

export const InsertAddress: React.FC<InsertAddressProps> = ({
  insertedAddress,
  territoryId,
  onContinue,
}) => {
  const [t] = useLanguage();

  const [address, setAddress] = useState(
    insertedAddress?.formattedAddress || ''
  );
  const [addressParts, setAddressParts] = useState<AddressParts | undefined>(
    insertedAddress
  );
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(
    !insertedAddress
  );
  const [addressError, setAddressError] = useState<AddressError | null>(null);
  const [preditions, setPredictions] = useState<PredictionsEntity[]>([]);

  const { getPlaces, getGeocoder, splitAddress } = useGoogleApiDesktop();
  const [getTerritoryId] = useIsValidAddressMutation();

  const onAddressChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setAddress(event.target.value);
    },
    []
  );

  const onWriting = useCallback(() => {
    setIsContinueDisabled(true);
    if (!isNil(address) && address.length > 0) {
      getPlaces(address, ({ predictions: autocompletePredictions }) => {
        setPredictions(autocompletePredictions ?? []);
      });
    } else {
      setPredictions([]);
    }
  }, [address, getPlaces]);

  const deleteInput = useCallback(() => {
    setAddress('');
    setPredictions([]);
    setIsContinueDisabled(true);
  }, []);

  const selectAddress = useCallback(
    (placeId: string) => {
      getGeocoder({ placeId }, (results: ResultsEntity[]) => {
        const addressSplitted = splitAddress(results![0]);
        if (addressSplitted.streetNumber) {
          getTerritoryId(addressSplitted.zipCode).then((res) => {
            const { successRes, errorRes } = parseRes(res);
            if (
              successRes.data &&
              (!territoryId || +territoryId === successRes.data.territoryId)
            ) {
              setIsContinueDisabled(false);
              setAddressError(null);
            } else if (
              (errorRes.error && errorRes.error.status === 404) ||
              !territoryId ||
              +territoryId !== successRes.data.territoryId
            ) {
              setAddressError(AddressError.AddressNotSupported);
            } else {
              // TODO: Handle general error
            }
          });
        } else {
          setAddressError(AddressError.MissingInfo);
        }
        setAddressParts(addressSplitted);
        setAddress(addressSplitted.formattedAddress);
        setPredictions([]);
      });
    },
    [getGeocoder, getTerritoryId, splitAddress, territoryId]
  );

  const goToNextStep = useCallback(() => {
    onContinue(addressParts!);
  }, [addressParts, onContinue]);

  return (
    <>
      <div className="insert-address--body">
        <h2 className="insert-address--subtitle">
          {t('searchAddressView.searchAddress')}
        </h2>
        <div className="insert-address--input-wrapper">
          <div className="insert-address--input-container">
            <input
              onChange={onAddressChange}
              onKeyUp={onWriting}
              value={address}
              type="text"
              className="insert-address--input"
              placeholder={t('searchAddressView.searchAddressInputPlaceholder')}
            />
            <div>
              {address === '' && <SearchIcon />}
              {address !== '' && (
                <button
                  onClick={deleteInput}
                  aria-label="Reset input"
                  className="insert-address--cancel-button"
                >
                  <CancelFilledIcon />
                </button>
              )}
            </div>
          </div>
        </div>
        {addressError && (
          <p className="error-message insert-address--error">
            {addressError === AddressError.MissingInfo
              ? t('searchAddressView.streetNumberError')
              : t('searchAddressView.addressNotSupportedError')}
          </p>
        )}
        <ul className="insert-address--list">
          {preditions.map((p) => {
            return (
              <React.Fragment key={p.place_id}>
                <li className="insert-address--list-item">
                  <button
                    className="insert-address--list-button"
                    onClick={() => selectAddress(p.place_id)}
                  >
                    {p.description}
                  </button>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </div>
      <div>
        <Button
          isDisabled={isContinueDisabled}
          label={t('searchAddressView.continue')}
          isPrimary
          onClick={goToNextStep}
        />
      </div>
    </>
  );
};
